import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, Plus, Receipt } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import { getProjects } from "../../../lib/projects";
import {
  getExpenses,
  getExpenseCategories,
  deleteExpense,
  billExpense,
  formatCents,
  type Expense,
} from "../../../lib/sales";
import { Btn, Card, EmptyState, MenuField, Modal, PageHead, PillTabs, SearchField, Toolbar, ToolbarGap, fmtNum, useToast, listCard } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ExpenseModal } from "./ExpenseModal";
import { COLS, ExpenseRow } from "./ExpenseRow";

/** The design's page size. The endpoint was being asked for a hundred rows with
 *  no pager under them, so anything past the hundredth was simply invisible. */
const PAGE_SIZE = 25;

/**
 * How many projects the filter offers and the row lookup can name.
 *
 * The projects endpoint caps `pageSize` at a hundred, and this list is not
 * scoped to one client, so a CRM that grows past a hundred projects will have
 * expenses whose project the row can only link to and not name (see the fallback
 * in `ExpenseRow`). One request rather than a loop is the deliberate trade: this
 * is a filter on a list page, not the project register.
 *
 * ARCHIVED PROJECTS COUNT AGAINST THIS HUNDRED, because the fetch below asks for
 * them on purpose - see the note there. Archiving a project does not touch its
 * expenses, so every one of them stays on this page and has to keep naming the
 * project it was booked to.
 */
const PROJECT_LIMIT = 100;

/**
 * Which side of `billable` the list is showing.
 *
 * Every one of these is answered by the endpoint (`billable=true|false`), not
 * by trimming the loaded page: a tab that filtered client-side would quietly
 * mean "of the rows on this page", and the total under it would disagree with
 * the rows above it. There is deliberately no "rebilled" tab - see the note by
 * the status column in ExpenseRow: the endpoint has no filter on `invoicedId`,
 * so that tab could only have been a client-side lie.
 */
type BillTab = "all" | "billable" | "not";
const BILL_TABS: BillTab[] = ["all", "billable", "not"];
const BILL_QUERY: Record<BillTab, string | undefined> = { all: undefined, billable: "true", not: "false" };

/** One figure from the expenses strip, the same tile the sales shell uses. */
function Stat({ label, value, sub }: { label: string; value: string; sub?: ReactNode }) {
  return (
    <div className="crm-q">
      <span>
        <span className="crm-q-lab block">{label}</span>
        <span className="crm-q-val block">{value}</span>
        {sub ? <span className="crm-prow-s block">{sub}</span> : null}
      </span>
    </div>
  );
}

export function ExpensesSection({ user }: { user: StaffUser }) {
  const t = useT();
  const nav = useNavigate();
  const toast = useToast();

  const [rows, setRows] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState("");
  // Two jobs, one request: the options for the filter below, and the id-to-name
  // lookup every row needs because the endpoint returns `projectId` as a bare
  // string with no relation behind it.
  const [projects, setProjects] = useState<{ id: string; name: string; archived: boolean }[]>([]);
  const [project, setProject] = useState("");
  const [tab, setTab] = useState<BillTab>("all");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [page, setPage] = useState(1);
  // Sums and count arrive with the page but cover the WHOLE filtered set, not
  // the rows on screen, which is what makes the strip safe to read next to a
  // pager.
  const [agg, setAgg] = useState({ sum: 0, tax: 0, total: 0 });
  const [editing, setEditing] = useState<Expense | null>(null);
  const [adding, setAdding] = useState(false);
  const [toDelete, setToDelete] = useState<Expense | null>(null);
  const [busy, setBusy] = useState(false);

  const canManage = userCanPlatform(user, "manage:crm.expenses");

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  const loadCategories = useCallback(() => {
    getExpenseCategories()
      .then((r) => setCategories(r.rows))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // `archived: "all"`, and the parameter is the whole point of this line.
  //
  // The register hides archived projects by default, and this request is not the
  // register: archiving a project leaves every expense booked to it exactly where
  // it was, so those rows are still on this page. Taking the default would drop
  // the project out of the lookup, and `ExpenseRow` would fall back to the word
  // "project" on a cost whose project we know perfectly well - a row that looks
  // like a lookup failure. It would also take the project out of the filter menu
  // while its expenses stay visible underneath it, which is a control that lies
  // about the list under it (the same argument `GET /task-tags` makes in
  // `crm-projects/tasks.routes.ts` for not narrowing to live projects).
  //
  // Archived ones are marked in the menu below rather than silently mixed in -
  // the rule the staff directory follows for people who have left.
  useEffect(() => {
    getProjects({ pageSize: PROJECT_LIMIT, archived: "all" })
      .then((res) =>
        setProjects(
          res.rows
            .map((p) => ({ id: p.id, name: p.name, archived: !!p.archivedAt }))
            // Live projects first, then by name: an archived one is still a legal
            // choice - its costs are on this page - but it is never the one the
            // operator is reaching for, and a directory that mixes them makes the
            // list of live projects longer to read for no gain.
            .sort((a, b) => Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name, "et")),
        ),
      )
      .catch(() => setProjects([]));
  }, []);

  // Any change to what is being asked for starts at the first page. Staying on
  // page 4 of a result set that now has one page shows an empty card and reads
  // as "the filter found nothing".
  useEffect(() => {
    setPage(1);
  }, [q, category, project, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getExpenses({
        page,
        pageSize: PAGE_SIZE,
        q: q || undefined,
        category: category || undefined,
        // Filtered by the endpoint, like every other control on this toolbar:
        // trimming the loaded page here would leave the three figures above
        // reporting the unfiltered set while the rows below showed one project.
        projectId: project || undefined,
        billable: BILL_QUERY[tab],
      });
      setRows(res.rows);
      setAgg({ sum: res.sumCents, tax: res.taxSumCents, total: res.total });
    } finally {
      setLoading(false);
    }
  }, [q, category, project, tab, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const catOptions = useMemo(
    () => [{ value: "", label: t("expenses.allCategories") }, ...categories.map((c) => ({ value: c, label: c }))],
    [categories, t],
  );

  // The archive mark goes INSIDE the label, because `MenuField` draws a row from
  // a string and nothing else - the same constraint `staffOptionLabel` works
  // around for a colleague who has left. Without it a retired project sits in the
  // list looking like an ordinary choice, and the operator picks it expecting the
  // costs of work that is still running.
  const projectOptions = useMemo(
    () => [
      { value: "", label: t("expenses.allProjects") },
      ...projects.map((p) => ({
        value: p.id,
        label: p.archived ? `${p.name} (${t("projects.archivedBadge")})` : p.name,
      })),
    ],
    [projects, t],
  );

  // The bare name, never the marked label: this map names the project in a table
  // cell where the archive is not the question being asked, and the cell is a
  // link to the project itself, which says so on arrival.
  const projectNames = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);

  async function confirmDelete() {
    if (!toDelete || busy) return;
    setBusy(true);
    try {
      await deleteExpense(toDelete.id);
      setToDelete(null);
      toast(t("expenses.deleted"));
      await load();
      // A category only exists because some expense carries it, so deleting the
      // last one has to drop it out of the filter as well.
      loadCategories();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Rebilling.
   *
   * The server answers 409 with the id of the invoice this expense already
   * produced, and the old code silently teleported the operator onto it - the
   * same click that usually creates something navigated somewhere else with no
   * explanation. The jump is kept, because that invoice is what the operator
   * was trying to reach, but it now says why it happened.
   */
  async function bill(expense: Expense) {
    try {
      const res = await billExpense(expense.id);
      toast(t("expenses.billedToast"));
      nav(`/sales/invoices/${res.invoice.id}`);
    } catch (e) {
      const existing = (e as { body?: { invoiceId?: string } }).body?.invoiceId;
      if (existing) {
        toast(t("expenses.alreadyBilledToast"));
        nav(`/sales/invoices/${existing}`);
        return;
      }
      toast(t("common.error"));
    }
  }

  const from = (page - 1) * PAGE_SIZE + 1;

  const addBtn = canManage ? (
    <Btn tone="brand" onClick={() => setAdding(true)}>
      <Plus className="size-4" />
      {t("expenses.add")}
    </Btn>
  ) : null;

  return (
    // One open menu at a time across every row.
    <PopHost>
      <PageHead title={t("expenses.title")} sub={t("expenses.intro")} actions={addBtn} />

      {/* Three figures, all of them the endpoint's own aggregates over the
          active filter rather than anything counted off the loaded page. They
          are currency-blind exactly as the endpoint computes them: it sums the
          cents column without grouping by currency, so a non-EUR expense would
          be added at face value. Everything booked here today is in euros. */}
      <Card className="crm-quick" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))" }}>
        <Stat
          label={t("expenses.totalSum")}
          value={formatCents(agg.sum)}
          sub={t("expenses.stats.grossSub", { v: formatCents(agg.sum + agg.tax) })}
        />
        <Stat label={t("expenses.stats.vat")} value={formatCents(agg.tax)} />
        <Stat label={t("expenses.stats.count")} value={fmtNum(agg.total)} />
      </Card>

      {/* Search and the two dropdowns on the left, the billable tabs pushed
          right - the toolbar shape every other list page in the design uses.
          Project sits beside category because the two narrow the same list from
          different sides: what kind of spend it was, and which piece of work it
          belongs to. */}
      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("expenses.search")} />
        <MenuField value={category} onChange={setCategory} label={t("expenses.category")} options={catOptions} />
        <MenuField value={project} onChange={setProject} label={t("expenses.project")} options={projectOptions} />
        <ToolbarGap />
        <PillTabs
          value={tab}
          onChange={setTab}
          tabs={BILL_TABS.map((v) => ({ value: v, label: t(`expenses.filter.${v}`) }))}
        />
      </Toolbar>

      <div className="crm-card">
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Receipt} title={t("expenses.empty")} body={t("expenses.emptyBody")} action={addBtn} />
        ) : (
          <>
            <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("expenses.col.category")}</span>
              <span>{t("expenses.col.date")}</span>
              <span>{t("expenses.col.vendor")}</span>
              <span>{t("expenses.col.client")}</span>
              <span>{t("expenses.project")}</span>
              <span>{t("expenses.col.amount")}</span>
              <span>{t("expenses.col.status")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0" style={listCard(COLS)}>
              {rows.map((ex) => (
                <ExpenseRow
                  key={ex.id}
                  expense={ex}
                  projectName={ex.projectId ? projectNames.get(ex.projectId) : undefined}
                  canManage={canManage}
                  onOpenInvoice={(id) => nav(`/sales/invoices/${id}`)}
                  onBill={bill}
                  onEdit={setEditing}
                  onDelete={setToDelete}
                />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("expenses.showing", { from, to: from + rows.length - 1, total: agg.total })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page * PAGE_SIZE >= agg.total} onClick={() => setPage((p) => p + 1)}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {adding || editing ? (
        <ExpenseModal
          expense={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            void load();
            loadCategories();
          }}
        />
      ) : null}

      {toDelete ? (
        <Modal
          title={t("expenses.deleteTitle")}
          label={t("expenses.deleteTitle")}
          onClose={() => setToDelete(null)}
          footer={
            <>
              <Btn onClick={() => setToDelete(null)} disabled={busy}>
                {t("expenses.cancel")}
              </Btn>
              <Btn tone="danger" onClick={() => void confirmDelete()} disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                {t("common.delete")}
              </Btn>
            </>
          }
        >
          {/* Naming the row being deleted, because the confirm is opened from a
              menu that has already closed over the row it belonged to. */}
          <p className="crm-sub">
            {t("expenses.deleteBody", {
              category: toDelete.category,
              amount: formatCents(toDelete.amountCents, toDelete.currency),
            })}
          </p>
        </Modal>
      ) : null}
    </PopHost>
  );
}
